import { useCallback, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { openToastAlert } from '../../../../../common/ToastAlert/toastAlert';
import { DELETE_CUSTOM_FILTER_ERROR_MESSAGE } from '../constants';
import { useDeleteCustomFilter } from './useDeleteCustomFilter';

export type PendingDeleteTarget = { id: string; name: string };

type UsePendingFilterDeleteOptions<T extends PendingDeleteTarget> = {
  onDeleted?: (target: T) => void;
};

type UsePendingFilterDeleteResult<T extends PendingDeleteTarget> = {
  pendingTarget: T | null;
  isDeleting: boolean;
  requestDelete: (target: T) => void;
  cancelDelete: () => void;
  confirmDelete: () => Promise<void>;
};

export function usePendingFilterDelete<T extends PendingDeleteTarget>({
  onDeleted,
}: UsePendingFilterDeleteOptions<T> = {}): UsePendingFilterDeleteResult<T> {
  const { deleteFilter, loading: isDeleting } = useDeleteCustomFilter();
  const [pendingTarget, setPendingTarget] = useState<T | null>(null);

  const requestDelete = useCallback((target: T): void => {
    setPendingTarget(target);
  }, []);

  const cancelDelete = useCallback((): void => {
    setPendingTarget(null);
  }, []);

  const confirmDelete = useCallback(async (): Promise<void> => {
    if (!pendingTarget) return;

    try {
      await deleteFilter(pendingTarget.id);
      unstable_batchedUpdates(() => {
        setPendingTarget(null);
        onDeleted?.(pendingTarget);
      });
    } catch (e) {
      openToastAlert({
        alertType: 'error',
        message: e instanceof Error ? e.message : DELETE_CUSTOM_FILTER_ERROR_MESSAGE,
      });
    }
  }, [pendingTarget, deleteFilter, onDeleted]);

  return { pendingTarget, isDeleting, requestDelete, cancelDelete, confirmDelete };
}
